import { CalendarToday, AccessTime } from '@mui/icons-material';
import { Paper, Typography, Box, Skeleton, SxProps } from '@mui/material';
import { accountSelectedSelector } from 'atoms';
import TimelineIcon from 'components/TimelineIcon';
import dayjs from 'dayjs';
import { useTimelineDescription } from 'hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Account } from 'types/Account';
import { Timeline, TimelineTypeEnum } from 'types/timeline.types';

function EventDetailsSummary({
  event,
  loading,
  type,
  account,
}: {
  event: Timeline | undefined;
  loading: boolean;
  type: TimelineTypeEnum;
  account: Account;
}) {
  const { t } = useTranslation(['events', '_common']);
  const accountSelected = useRecoilValue(accountSelectedSelector);

  const { title, description } = useTimelineDescription({ event });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, flex: '0 0 auto' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t('events:events_details')}</Typography>
        {!accountSelected && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'inline-block' }}>
              {t('_common:account')}:
            </Typography>
            <Typography variant="body2" sx={{ ml: 0.5, display: 'inline-block' }}>
              {account.accountCode} - {account.displayName}
            </Typography>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex' }}>
        <span>
          <TimelineIcon {...{ type }} />
        </span>
        <EventDetailsSummaryItem
          label={t('_common:event')}
          value={event ? title : <Skeleton variant="text" />}
          sx={{ flex: 1 }}
          caption={description}
        />
        <EventDetailsSummaryItem
          label={t('_common:date')}
          value={
            event ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ width: '0.8rem', mr: 0.5 }} />
                {dayjs(event.date).format('L')}
              </Box>
            ) : (
              <Skeleton variant="text" />
            )
          }
        />
        <EventDetailsSummaryItem
          label={t('_common:time')}
          value={
            event ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTime sx={{ width: '0.8rem', mr: 0.5 }} />
                {dayjs(event.date).format('LTS')}
              </Box>
            ) : (
              <Skeleton variant="text" />
            )
          }
        />
        {type !== TimelineTypeEnum.LOG && (
          <>
            <EventDetailsSummaryItem
              label={t('_common:code')}
              value={event ? event.event?.code || '' : <Skeleton variant="text" />}
            />
            <EventDetailsSummaryItem
              label={t('_common:auxiliary')}
              value={event ? event.event?.auxiliary || '' : <Skeleton variant="text" />}
            />
          </>
        )}
      </Box>
    </Paper>
  );
}

function EventDetailsSummaryItem({
  label,
  value,
  sx,
  caption,
}: {
  label: string;
  value: string | JSX.Element;
  sx?: SxProps;
  caption?: string | null;
}) {
  return (
    <Box sx={{ mr: 2, ...sx }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography>{value}</Typography>
      {caption && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
}

export default EventDetailsSummary;
